/**
 * OuraConsent — Public page for patient Oura Ring authorization.
 *
 * Routes:
 *   /_oura/connect/{invite_token}  → shows consent page + "Connect with Oura" button
 *   /_oura/callback                → handles Oura OAuth redirect, exchanges code for tokens
 */
import React, { useEffect, useState } from "react";
import { API_BASE, OURA_CLIENT_ID } from "../config";

type Phase = "consent" | "exchanging" | "success" | "error";

const OuraConsent: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("consent");
  const [errorMsg, setErrorMsg] = useState("");
  const path = window.location.pathname;
  const isCallback = path.startsWith("/_oura/callback");

  // Extract invite token from URL
  const inviteToken = isCallback
    ? new URLSearchParams(window.location.search).get("state") || ""
    : path.replace("/_oura/connect/", "");

  // Handle Oura callback (exchange code) — guard against StrictMode double-fire
  const exchangedRef = React.useRef(false);
  useEffect(() => {
    if (!isCallback || exchangedRef.current) return;
    exchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code") || "";
    const state = params.get("state") || "";
    const ouraError = params.get("error") || "";

    if (ouraError) {
      setPhase("error");
      setErrorMsg(`Oura authorization failed: ${ouraError}`);
      return;
    }

    if (!code || !state) {
      setPhase("error");
      setErrorMsg("Missing authorization code or state.");
      return;
    }

    setPhase("exchanging");

    const origin = window.location.origin;
    const redirectUri = `${origin}/_oura/callback`;

    fetch(`${API_BASE}/oura/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        invite_token: state,
        redirect_uri: redirectUri,
      }),
    })
      .then(async (resp) => {
        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.error || `HTTP ${resp.status}`);
        }
        setPhase("success");
      })
      .catch((err) => {
        setPhase("error");
        setErrorMsg(err.message || "Token exchange failed");
      });
  }, [isCallback]);

  const handleConnect = () => {
    if (!OURA_CLIENT_ID) {
      setPhase("error");
      setErrorMsg("Oura client ID not configured.");
      return;
    }

    const origin = window.location.origin;
    const redirectUri = `${origin}/_oura/callback`;
    const scope = "personal daily heartrate";

    const params = new URLSearchParams({
      response_type: "code",
      client_id: OURA_CLIENT_ID,
      redirect_uri: redirectUri,
      scope,
      state: inviteToken,
    });

    window.location.href = `https://cloud.ouraring.com/oauth/authorize?${params.toString()}`;
  };

  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, var(--color-subsurface) 0%, var(--color-border) 100%)",
    padding: 24,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  };

  const cardStyle: React.CSSProperties = {
    width: "min(460px, 92vw)",
    padding: "32px 28px",
    borderRadius: 16,
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
    textAlign: "center",
  };

  const btnStyle: React.CSSProperties = {
    marginTop: 20,
    padding: "12px 24px",
    background: "var(--color-brand)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  };

  if (phase === "exchanging") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: 0, fontSize: 20, color: "var(--color-title)" }}>
            Connecting your Oura Ring...
          </h2>
          <p style={{ marginTop: 12, color: "var(--color-muted)", fontSize: 14 }}>
            Please wait while we securely link your account.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "success") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>&#x2705;</div>
          <h2 style={{ margin: 0, fontSize: 20, color: "var(--color-title)" }}>
            Oura Ring Connected
          </h2>
          <p style={{ marginTop: 12, color: "var(--color-muted)", fontSize: 14 }}>
            Your Oura Ring data is now shared with your therapist.
            You can close this page.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>&#x274C;</div>
          <h2 style={{ margin: 0, fontSize: 20, color: "var(--color-title)" }}>
            Connection Failed
          </h2>
          <p style={{ marginTop: 12, color: "var(--color-danger)", fontSize: 14 }}>
            {errorMsg || "An unknown error occurred."}
          </p>
          <p style={{ marginTop: 8, color: "var(--color-muted)", fontSize: 13 }}>
            Please ask your therapist for a new invite link.
          </p>
        </div>
      </div>
    );
  }

  // Consent phase
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--color-title)" }}>
          Connect Your Oura Ring
        </h2>
        <p style={{ marginTop: 12, color: "var(--color-muted)", fontSize: 14, lineHeight: 1.6 }}>
          Your therapist has invited you to share your Oura Ring data
          (sleep, readiness, and HRV) to help track your wellbeing
          alongside therapy sessions.
        </p>
        <p style={{ marginTop: 8, color: "var(--color-muted)", fontSize: 13 }}>
          Your data is encrypted and only visible to your therapist.
        </p>
        <button style={btnStyle} onClick={handleConnect}>
          Connect with Oura
        </button>
        {!inviteToken && (
          <p style={{ marginTop: 16, color: "var(--color-danger)", fontSize: 12 }}>
            Invalid or missing invite link. Please ask your therapist for a new one.
          </p>
        )}
      </div>
    </div>
  );
};

export default OuraConsent;
